<template>
    <div class="info-modal animate-in" tabindex="-1" :class="[size]">
        <div class="info-modal-bg" @click="close"></div>
        <div class="info-modal-box">
            <div v-if="$slots.header || title" class="info-modal-box-header">
                <slot name="pre-header"/>
                <h2 v-if="title" class="info-modal-title mb-0">{{ title }}</h2>
                <slot name="header"/>
                <button v-if="closable" @click="close" aria-label="Close Modal" class="info-modal-close">
                    <icon icon="x"/>
                </button>
            </div>

            <div class ="info-modal-box-content">
                <slot/>
            </div>

            <div v-if="$slots.footer" class="info-modal-box-footer">
                <slot name="footer"/>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapMutations } from 'vuex';
    import ClickOutside from 'vue-click-outside';

    import Icon from '@/components/ui/Icon';

    export default {
        name: 'Modal',
        directives: {
            ClickOutside,
        },
        components: {
            Icon,
        },
        data() {
            return {
                mainContent: document.querySelector('.page-wrapper'),
            };
        },
        props: {
            title: {
                required: false,
                type: String,
                default: null,
            },
            closable: {
                required: false,
                default: true,
                type: Boolean,
            },
            size: {
                required: false,
                default: 'medium',
                type: String,
            },
        },
        created() {
            this.mainContent.style.top = -window.pageYOffset + 'px';
            document.body.classList.add('info-modal-open');
        },
        destroyed() {
            const scrollOffset = Math.abs(parseFloat(this.mainContent.style.top));
            document.body.classList.remove('info-modal-open');
            this.mainContent.style.removeProperty('top');
            window.scrollTo(0, scrollOffset);
        },
        methods: {
            ...mapMutations('modal', [
                'setModal',
            ]),

            close() {
                if (!this.closable) return;
                this.setModal(null);
                this.$emit('close');
            },
        },
    };
</script>

<style lang="scss">
    body,
    html,
    #app {
        height: 100%;
    }

    .info-modal {
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        z-index: 100;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-height: 100%;

        &-button {
            flex-grow: 1;
            margin-right: 10px;

            &:last-child {
                margin-right: 0;
            }
        }

        &-back-button {
            display: inline-flex;
            align-items: center;
            padding: 0 16px;
            margin-bottom: 16px;
            font-size: 14px;
            font-weight: 500;
            line-height: 24px;
            color: #fff;
            white-space: nowrap;
            background: $athens-gray;
            border-radius: 12px;
        }

        &-divider {
            height: 1px;
            margin: 30px -32px;
            background: $athens-gray;
        }

        &-open .page-wrapper {
            position: fixed;
            display: block;
            width: 100%;
        }

        &-title {
            font-size: 20px;
            font-weight: 600;
            color: $mine-shaft;
        }

        &-bg {
            position: fixed;
            top: 0;
            left: 0;
            z-index: 1;
            width: 100%;
            height: 100%;
            background: rgba($mine-shaft, 0.7);

            .animate-in & {
                animation: animateInModalBg 0.5s both;
            }
        }

        &-box {
            position: relative;
            z-index: 2;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            width: calc(100% - 16px);
            margin: 8px;
            background: #fff;
            border-radius: 3px;
            box-shadow: 1px 1px 2px rgba($mine-shaft, 0.05);

            @include respond-to(md) {
                flex-grow: 0;
                max-width: 600px;
                margin: 32px auto;

                .huge & {
                    width: calc(100% - 64px);
                    max-width: 1600px;
                }
            }

            &-header {
                position: relative;
                padding: 16px 44px 16px 16px;
                border-bottom: 1px solid $athens-gray;

                @include respond-to(medium) {
                    padding: 32px 46px 32px 32px;
                }
            }

            &-content {
                flex: 1 1 auto;
                padding: 16px;

                @include respond-to(md) {
                    padding: 32px;
                }
            }

            &-footer {
                padding: 16px;
                border-radius: 0 0 3px 3px;
            }

            .animate-in & {
                animation: animateInModalBox 0.3s 0.2s both;
            }
        }

        &-close {
            position: absolute;
            top: 0;
            right: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            font-size: 24px;
            line-height: 48px;
            text-align: center;
            transition: color 0.3s;
        }
    }

    @keyframes animateInModalBg {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    @keyframes animateInModalBox {
        from {
            opacity: 0;
            transform: scale(0.8);
        }
        to {
            opacity: 1;
            transform: scale(1);
        }
    }
</style>
